import React, { forwardRef, useImperativeHandle, useMemo } from 'react';
import { i18n } from 'src/common';
import { CheckboxOptionGroup, Card } from 'src/components';
import { FieldConfig, Form, FormRef } from './form';
import { Validation } from './validation';
import type { CheckboxOptionGroupOption } from 'src/components/inputs/checkboxOptionGroup.input';

export type InsuranceNeedsFormType = 'selectedPropertyIds' | 'selectedRiskIds';
export const allInsuranceNeedsFormItems: Array<InsuranceNeedsFormType> = ['selectedPropertyIds', 'selectedRiskIds'];

export interface InsuranceNeedsFormData {
  selectedPropertyIds?: string[];
  selectedRiskIds?: string[];
}

interface InsuranceNeedsFormProps {
  data: InsuranceNeedsFormData;
  formItems?: Array<InsuranceNeedsFormType>,
  onSubmit: (data: InsuranceNeedsFormData) => void;
}

export const InsuranceNeedsForm = forwardRef<FormRef, InsuranceNeedsFormProps>(
  ({ data, onSubmit, formItems = allInsuranceNeedsFormItems }, ref) => {
    const formRef = React.useRef<FormRef>(null);

    const propertyOptions: CheckboxOptionGroupOption[] = useMemo(() => [
      { id: 'building', label: i18n.t('components.forms.insuranceNeeds.building'), description: i18n.t('components.forms.insuranceNeeds.buildingDesc'), isPopular: false },
      { id: 'household', label: i18n.t('components.forms.insuranceNeeds.household'), description: i18n.t('components.forms.insuranceNeeds.householdDesc'), isPopular: true },
    ], []);

    const riskOptions: CheckboxOptionGroupOption[] = useMemo(() => [
      { id: 'burglaryAndRobbery', label: i18n.t('components.forms.insuranceNeeds.burglaryAndRobbery'), description: i18n.t('components.forms.insuranceNeeds.burglaryAndRobberyDesc') },
      { id: 'fireStormHailAndExplosionInsurance', label: i18n.t('components.forms.insuranceNeeds.fireStormHailAndExplosionInsurance'), description: i18n.t('components.forms.insuranceNeeds.fireStormHailAndExplosionInsuranceDesc') },
      { id: 'flood', label: i18n.t('components.forms.insuranceNeeds.flood'), description: i18n.t('components.forms.insuranceNeeds.floodDesc') },
      { id: 'pipeBurstAndWaterLeakage', label: i18n.t('components.forms.insuranceNeeds.pipeBurstAndWaterLeakage'), description: i18n.t('components.forms.insuranceNeeds.pipeBurstAndWaterLeakageDesc') },
      { id: 'glassBreakage', label: i18n.t('components.forms.insuranceNeeds.glassBreakage'), description: i18n.t('components.forms.insuranceNeeds.glassBreakageDesc'), isPopular: true },
      { id: 'earthquake', label: i18n.t('components.forms.insuranceNeeds.earthquake'), description: i18n.t('components.forms.insuranceNeeds.earthquakeDesc'), isPopular: true },
      { id: 'indirectLightningStrike', label: i18n.t('components.forms.insuranceNeeds.indirectLightningStrike'), description: i18n.t('components.forms.insuranceNeeds.indirectLightningStrikeDesc') },
      { id: 'otherRisks', label: i18n.t('components.forms.insuranceNeeds.otherRisks'), description: i18n.t('components.forms.insuranceNeeds.otherRisksDesc') },
    ], []);

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        if (formRef.current) {
          formRef.current.submitForm();
        }
      },
      getValues: () => formRef.current?.getValues?.() ?? {},
      validateForm: () => formRef.current?.validateForm?.() ?? false,
      setFieldValue: (fieldName: string, value: unknown, validation?: boolean) => {
        formRef.current?.setFieldValue?.(fieldName, value, validation);
      },
    }));

    const fields: FieldConfig<InsuranceNeedsFormData>[] = useMemo(() => [
      {
        name: 'selectedPropertyIds',
        label: i18n.t('components.forms.insuranceNeeds.whatToInsure'),
        component: CheckboxOptionGroup,
        options: propertyOptions as unknown as { label: string; value: unknown }[],
        wrapper: Card,
        validations: [
          {
            type: 'atLeastOneOf',
            message: i18n.t('components.forms.insuranceNeeds.atLeastOneInsuranceType')
          } as Validation,
        ],
      },
      {
        name: 'selectedRiskIds',
        label: i18n.t('components.forms.insuranceNeeds.whichRisks'),
        component: CheckboxOptionGroup,
        options: riskOptions as unknown as { label: string; value: unknown }[],
        wrapper: Card,
        validations: [
          {
            type: 'atLeastOneOf',
            message: i18n.t('components.forms.insuranceNeeds.atLeastOneRisk'),
          } as Validation,
        ],
      },
    ], [propertyOptions, riskOptions]).filter((item) => formItems.includes(item.name as InsuranceNeedsFormType));

    const handleSubmit = (values: Record<string, unknown>) => {
      const raw = values as Partial<InsuranceNeedsFormData>;
      const convertedValues: InsuranceNeedsFormData = {
        selectedPropertyIds: Array.isArray(raw.selectedPropertyIds) ? raw.selectedPropertyIds : [],
        selectedRiskIds: Array.isArray(raw.selectedRiskIds) ? raw.selectedRiskIds : [],
      };
      onSubmit(convertedValues);
    };

    return (
      <Form
        ref={formRef}
        fields={fields}
        initialValues={data}
        onSubmit={handleSubmit}
        config={{ gap: 44 }}
      />
    );
  }
);
